const { REACT_APP_S3_CLIP_BUCKET } = process.env;

// CS2 values from common.Player.RankType(); 6 was CS:GO's Competitive.
// Anything that is not Premier or Wingman falls through to the matchmaking
// skill-group icons, so Competitive is listed for the record, not branched on.
const ERankType = {
  COMPETITIVE: 12,
  WINGMAN: 7,
  PREMIER: 11,
};

// Wingman uses the same skill groups as competitive.
const SKILL_GROUPS: Record<number, string> = {
  1: "Silver I",
  2: "Silver II",
  3: "Silver III",
  4: "Silver IV",
  5: "Silver Elite",
  6: "Silver Elite Master",
  7: "Gold Nova I",
  8: "Gold Nova II",
  9: "Gold Nova III",
  10: "Gold Nova Master",
  11: "Master Guardian I",
  12: "Master Guardian II",
  13: "Master Guardian Elite",
  14: "Distinguished Master Guardian",
  15: "Legendary Eagle",
  16: "Legendary Eagle Master",
  17: "Supreme Master First Class",
  18: "The Global Elite",
};

// Premier has no rank names, just CS Rating banded into the colors Valve
// paints the badge with. Each band is 5000 wide.
const PREMIER_TIERS: { min: number; color: string }[] = [
  { min: 30000, color: "#ffd700" },
  { min: 25000, color: "#eb4b4b" },
  { min: 20000, color: "#d32ce6" },
  { min: 15000, color: "#8847ff" },
  { min: 10000, color: "#4b69ff" },
  { min: 5000, color: "#5e98d9" },
  { min: 0, color: "#b0c3d9" },
];

const RANK_ICON_BASE = `${REACT_APP_S3_CLIP_BUCKET}/static/web/match_page/rank_icons`;

export type TRankDisplay = {
  label: string;
  color: string;
  iconUrl?: string;
  isPremier?: boolean;
};

// Null when the match did not report a rank, which is everything outside
// Valve matchmaking.
export const getRankDisplay = (
  rank?: number | null,
  rankType?: number | null,
): TRankDisplay | null => {
  // 0.svg is the "?" plate. Covers both a genuinely unranked player and a
  // match that reported no rank at all, like anything off Valve matchmaking.
  if (!rank) return skillGroupDisplay(0, "Unranked", rankType);

  if (rankType === ERankType.PREMIER) {
    const tier =
      PREMIER_TIERS.find((entry) => rank >= entry.min) ||
      PREMIER_TIERS[PREMIER_TIERS.length - 1];
    return { label: rank.toLocaleString(), color: tier.color, isPremier: true };
  }

  const name = SKILL_GROUPS[rank];
  if (!name) return null;
  return skillGroupDisplay(rank, name, rankType);
};

const skillGroupDisplay = (
  rank: number,
  label: string,
  rankType?: number | null,
): TRankDisplay => {
  const folder = rankType === ERankType.WINGMAN ? "wingman" : "matchmaking";
  return {
    label,
    iconUrl: `${RANK_ICON_BASE}/${folder}/${rank}.svg`,
    color: "#b0c3d9",
  };
};

export type TRankMove = {
  promoted: boolean;
  title: string;
  // Premier only: the rating swing itself, which is the thing Premier players
  // actually track. Skill-group moves have no number to report.
  delta?: number;
};

export const getRankMove = (stats: {
  rankOld?: number | null;
  rankNew?: number | null;
  rankType?: number | null;
}): TRankMove | null => {
  const { rankOld, rankNew, rankType } = stats;
  if (!rankOld || !rankNew || rankOld === rankNew) return null;

  // Premier moves every match, and the size of the move is the point: a bare
  // arrow was hidden here precisely because it said nothing. The number does.
  if (rankType === ERankType.PREMIER) {
    const delta = rankNew - rankOld;
    return {
      promoted: delta > 0,
      title: `${delta > 0 ? "Gained" : "Lost"} ${Math.abs(
        delta,
      )} rating: ${rankOld} to ${rankNew}`,
      delta,
    };
  }

  const promoted = rankNew > rankOld;
  const from = getRankDisplay(rankOld, rankType);
  const to = getRankDisplay(rankNew, rankType);
  return {
    promoted,
    title: `${promoted ? "Promoted" : "Demoted"}: ${
      from?.label ?? rankOld
    } to ${to?.label ?? rankNew}`,
  };
};
